import type { Ref } from 'react';
import { Box } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { resolveTone, statusToSeverity, type StatusSeverity } from './statusSeverity';

export interface StatusChipProps {
  /**
   * Visible text. Screens pass translated copy here. Falls back to `status`
   * when omitted so a bare `status` still renders something.
   */
  label?: string;
  /** Semantic status code; used to derive the tone when `severity` is absent. */
  status?: string;
  /** Explicit tone override (wins over `status`). */
  severity?: StatusSeverity;
  /** Leading tone dot. */
  dot?: boolean;
  /** Allow the label to wrap onto multiple lines (rare). */
  allowWrap?: boolean;
  ref?: Ref<HTMLSpanElement>;
}

/**
 * Semantic-colored status pill. Background is `main` at 8% alpha, border is
 * `main` at 28%, text is the tone's `dark` shade — the Polaris light-mode
 * treatment. Pill radius comes from `sx` (999) not the theme's chip radius (4).
 */
export function StatusChip({
  label,
  status,
  severity,
  dot = false,
  allowWrap = false,
  ref,
}: StatusChipProps) {
  const theme = useTheme();
  const resolved = severity ?? (status ? statusToSeverity(status) : 'neutral');
  const tone = resolveTone(theme, resolved);
  const text = label ?? status ?? '';

  return (
    <Box
      component="span"
      ref={ref}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.625,
        px: 1,
        height: 22,
        borderRadius: 999,
        bgcolor: alpha(tone.main, 0.08),
        border: `1px solid ${alpha(tone.main, 0.28)}`,
        color: tone.dark,
        fontFamily: theme.typography.button.fontFamily,
        fontSize: 12,
        fontWeight: 700,
        lineHeight: 1,
        whiteSpace: allowWrap ? 'normal' : 'nowrap',
      }}
    >
      {dot && (
        <Box
          component="span"
          sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: tone.main, flexShrink: 0 }}
        />
      )}
      {text}
    </Box>
  );
}
